import React, { useEffect, useState } from "react";
import { useRefresh } from "react-admin";
import { baseApiClient } from "data/modules/api/api.client";
import { logError } from "utils/error";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { useOfferingVariationListStore } from "features/OfferingVariations/pages/OfferingVariationsList/store/useOfferingVariationList.store";
import { GET_MEMBERSHIP_OFFERING_VARIATIONS_PATHNAME } from "features/OfferingVariations/OfferingVariations.apis";
import { VARIATION_KYC_DISPLAY_STRINGS } from "features/OfferingVariations/OfferingVariations.constants";
import { updateVariationKycOverrideApi } from "features/OfferingVariations/utils/updateVariationKycOverride";
import {
  PER_TIER_KYC_CONTEXT,
  PER_TIER_KYC_UPLOAD,
} from "./PerTierKyc.constants";
import { getRecordKycOverride, isSameKycOverride } from "./PerTierKyc.utils";
import { PerTierKycModal } from "./PerTierKycModal";
import { KycConfirmDialog } from "./KycConfirmDialog";
import { usePerTierKyc } from "./usePerTierKyc";
import { usePerTierKycOffering } from "./usePerTierKycOffering";

export const VariationKycContainer = () => {
  const refresh = useRefresh();
  const { notify } = useNotifications();
  const open = useOfferingVariationListStore((state) => state.isOpenKycModal);
  const setOpen = useOfferingVariationListStore(
    (state) => state.setIsOpenKycModal
  );
  const selectedVariation = useOfferingVariationListStore(
    (state) => state.selectedVariation
  );
  const offeringUuid = useOfferingVariationListStore(
    (state) => state.offeringUuid
  );
  const offeringDetails = useOfferingVariationListStore(
    (state) => state.offeringDetails
  );
  const { offering, loading, fetchOfferingKyc } = usePerTierKycOffering();
  const [loadingRecord, setLoadingRecord] = useState(false);
  const kyc = usePerTierKyc({
    onPersist: async ({ record, override }) => {
      try {
        const response = await updateVariationKycOverrideApi({
          offeringUuid,
          offeringType: offeringDetails?.type,
          record,
          override,
        });
        notify(
          response?.message ?? VARIATION_KYC_DISPLAY_STRINGS.update_success,
          notification_color_keys.success
        );
      } catch (error) {
        logError({
          error,
          occuredAt:
            "src/features/ManageOfferings/modules/PerTierKyc/VariationKycContainer.jsx",
          when: "on updateVariationKycOverride",
        });
        notify(
          error?.response?.data?.message ??
            error?.message ??
            SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        throw error;
      }
      setOpen(false);
      refresh();
    },
    onNoChange: () => setOpen(false),
  });

  useEffect(() => {
    if (!open || !selectedVariation) return;
    let cancelled = false;
    // Open immediately with the list-row snapshot, then refresh from the
    // list API so the modal never edits (or overwrites) a stale override —
    // mirrors MembershipKycContainer's getIndividualMembership refresh.
    kyc.openForRecord(selectedVariation);
    fetchOfferingKyc({ listingUuid: offeringUuid });
    (async () => {
      setLoadingRecord(true);
      try {
        const response = await baseApiClient.get(
          GET_MEMBERSHIP_OFFERING_VARIATIONS_PATHNAME({
            offeringUuid,
          })
        );
        const listData = response?.data?.data ?? response?.data ?? {};
        const variants = listData?.offering_variants ?? [];
        const freshRecord = variants.find(
          (variant) => variant?.uuid === selectedVariation?.uuid
        );
        if (cancelled || !freshRecord) return;
        // List data was already fresh — re-opening would pointlessly reset
        // the draft (and could wipe edits made while the fetch was in
        // flight), so keep the snapshot draft untouched.
        if (
          isSameKycOverride(
            getRecordKycOverride(freshRecord),
            getRecordKycOverride(selectedVariation)
          )
        ) {
          return;
        }
        const mergedRecord = { ...selectedVariation, ...freshRecord };
        if (
          !getRecordKycOverride(mergedRecord) &&
          getRecordKycOverride(selectedVariation)
        ) {
          mergedRecord.metadata = selectedVariation.metadata;
          if (selectedVariation.kyc_override !== undefined) {
            mergedRecord.kyc_override = selectedVariation.kyc_override;
          }
        }
        kyc.openForRecord(mergedRecord);
      } catch (error) {
        logError({
          error,
          customErrorMessage: "Error while fetching variation KYC details",
          when: "opening VariationKycContainer",
          occuredAt:
            "src/features/ManageOfferings/modules/PerTierKyc/VariationKycContainer.jsx",
          severity: "blocker",
        });
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedVariation?.uuid]);

  const handleClose = () => {
    kyc.closeAll();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <>
      <PerTierKycModal
        open={open && !kyc.confirming}
        recordTitle={kyc.record?.title ?? selectedVariation?.title ?? ""}
        context={PER_TIER_KYC_CONTEXT.VARIATION}
        offering={offering}
        draft={kyc.draft}
        errors={kyc.errors}
        saving={kyc.saving}
        loadingOffering={loading || loadingRecord}
        uploadBasePath={`${PER_TIER_KYC_UPLOAD.BASE_PATH_PREFIX}/${offeringUuid}/${kyc.record?.uuid}`}
        onModeChange={kyc.handleModeChange}
        onMethodChange={kyc.handleMethodChange}
        onEnabledChange={kyc.handleEnabledChange}
        onPlacementChange={kyc.handlePlacementChange}
        onContractUrlChange={kyc.handleContractUrlChange}
        onWorkflowChange={kyc.handleWorkflowChange}
        onContractFile={kyc.handleContractFile}
        onContractRemove={kyc.handleContractRemove}
        onClose={handleClose}
        onSave={kyc.handleSave}
      />
      <KycConfirmDialog
        open={open && kyc.confirming}
        acknowledged={kyc.acknowledged}
        confirming={kyc.saving}
        onAckChange={kyc.setAcknowledged}
        onBack={kyc.backToEdit}
        onConfirm={kyc.handleConfirm}
      />
    </>
  );
};
